import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FlaskConical, Laptop, GraduationCap, HeartPulse, MapPin, Briefcase, Send, LoaderCircle } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
    { icon: FlaskConical, title: "AI CoE Research Time", body: "Dedicated hours every sprint to prototype and publish with our AI Center of Excellence." },
    { icon: Laptop, title: "Hybrid & Flexible", body: "Work from our Bengaluru studio or remotely, outcomes matter, not desk time." },
    { icon: GraduationCap, title: "Learning Budget", body: "Annual budget for certifications, courses and conferences, plus sponsored research." },
    { icon: HeartPulse, title: "Health & Family Cover", body: "Comprehensive insurance for you and your family, plus generous leave policies." },
];

const ROLES = [
    { title: "Senior AI/ML Engineer", location: "Bengaluru", type: "Full-time", team: "AI CoE" },
    { title: "Cloud Solutions Architect", location: "Bengaluru", type: "Full-time", team: "Cloud" },
    { title: "Cybersecurity Consultant", location: "Hybrid", type: "Full-time", team: "Security" },
    { title: "Data Engineer (Kafka / Spark)", location: "Bengaluru", type: "Full-time", team: "Data" },
    { title: "Full-Stack Developer (React / Python)", location: "Remote", type: "Full-time", team: "Engineering" },
    { title: "Business Development Manager", location: "Bengaluru", type: "Full-time", team: "Growth" },
];

const initialForm = { name: "", email: "", phone: "", role: ROLES[0].title, resume_link: "", message: "" };

export default function CareersPage() {
    const [form, setForm] = useState(initialForm);
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const applyFor = (role) => {
        setForm((f) => ({ ...f, role }));
        scrollToId("#careers-form");
    };

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/applications`, form);
            toast.success("Application received. Our hiring team will review it within five business days.");
            setForm(initialForm);
        } catch (err) {
            toast.error("Could not submit your application. Please try again.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-volt/60 focus:bg-white/[0.06] focus:shadow-[0_0_20px_rgba(0,240,255,0.08)]";

    return (
        <PageShell
            eyebrow="Careers at Syntera"
            title="Build what's next."
            accent="Grow with Syntera."
            sub="Join a team engineering the autonomous enterprise from Bengaluru, deep-tech work, real ownership, and a culture of curiosity."
            testid="careers-page"
        >
            <section className="py-20 sm:py-28">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <Eyebrow>Why Syntera</Eyebrow>
                        <h2 className="mb-4 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="careers-perks-heading">
                            Do the best work of your career
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {PERKS.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.08}>
                                <div data-testid={`careers-perk-${i}`} className="card-glow-border h-full rounded-2xl border border-white/[0.08] bg-card2/50 p-7">
                                    <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-volt/20 bg-volt/10 text-volt">
                                        <p.icon size={20} />
                                    </div>
                                    <h3 className="mb-2 font-syne text-lg font-semibold text-white">{p.title}</h3>
                                    <p className="text-sm leading-relaxed text-slate-400">{p.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-white/[0.06] bg-panel/40 py-20 sm:py-24" data-testid="careers-roles">
                <div className="mx-auto max-w-5xl px-5 sm:px-8">
                    <Reveal>
                        <Eyebrow>Open Roles</Eyebrow>
                        <h2 className="mb-4 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="careers-roles-heading">
                            Find your seat on the team
                        </h2>
                    </Reveal>
                    <div className="mt-10 space-y-3">
                        {ROLES.map((r, i) => (
                            <Reveal key={r.title} delay={i * 0.06}>
                                <div
                                    data-testid={`careers-role-${i}`}
                                    className="card-glow-border flex flex-col gap-4 rounded-2xl border border-white/[0.08] bg-card2/50 p-6 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div>
                                        <h3 className="font-syne text-lg font-semibold text-white">{r.title}</h3>
                                        <div className="mt-1.5 flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                                            <span className="flex items-center gap-1.5"><MapPin size={12} className="text-volt/70" />{r.location}</span>
                                            <span className="flex items-center gap-1.5"><Briefcase size={12} className="text-volt/70" />{r.type}</span>
                                            <span className="rounded-full border border-volt/25 bg-volt/10 px-2.5 py-0.5 text-volt">{r.team}</span>
                                        </div>
                                    </div>
                                    <button
                                        data-testid={`careers-apply-${i}`}
                                        onClick={() => applyFor(r.title)}
                                        className="shrink-0 rounded-full border border-volt/40 px-6 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-volt transition-all duration-300 hover:bg-volt hover:text-[#07090E]"
                                    >
                                        Apply Now
                                    </button>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section id="careers-form" className="py-20 sm:py-28" data-testid="careers-apply-section">
                <div className="mx-auto max-w-3xl px-5 sm:px-8">
                    <Reveal className="text-center">
                        <Eyebrow>Apply</Eyebrow>
                        <h2 className="mb-4 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="careers-form-heading">
                            Tell us about yourself
                        </h2>
                        <p className="text-sm text-slate-400 sm:text-base">
                            No cover letter theatrics, just your details, a link to your work, and why you're excited.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <form onSubmit={submit} data-testid="careers-application-form" className="mt-10 rounded-3xl border border-white/[0.08] bg-card2/50 p-7 sm:p-9">
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="careers-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Name *</label>
                                    <input id="careers-name" data-testid="careers-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="careers-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Email *</label>
                                    <input id="careers-email" data-testid="careers-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="careers-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Phone</label>
                                    <input id="careers-phone" data-testid="careers-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="careers-role" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Role *</label>
                                    <select id="careers-role" data-testid="careers-role-select" required value={form.role} onChange={set("role")} className={`${inputCls} appearance-none`}>
                                        {ROLES.map((r) => (
                                            <option key={r.title} value={r.title} className="bg-[#0D111A]">{r.title}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="mt-5">
                                <label htmlFor="careers-resume" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Resume / Portfolio Link</label>
                                <input id="careers-resume" data-testid="careers-resume-input" type="url" value={form.resume_link} onChange={set("resume_link")} placeholder="https://linkedin.com/in/you or a Drive/Dropbox link" className={inputCls} />
                            </div>
                            <div className="mt-5">
                                <label htmlFor="careers-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Why Syntera?</label>
                                <textarea id="careers-message" data-testid="careers-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="A few lines about what you want to build..." className={`${inputCls} resize-none`} />
                            </div>
                            <button
                                type="submit"
                                data-testid="careers-submit-btn"
                                disabled={sending}
                                className="group mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-volt px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-[#07090E] transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.45)] disabled:opacity-60"
                            >
                                {sending ? <LoaderCircle size={15} className="animate-spin" /> : <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />}
                                {sending ? "Submitting..." : "Submit Application"}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </section>
        </PageShell>
    );
}
